import { useMemo } from 'react';

import { View } from 'native-base';
import { useIntl } from 'react-intl';
import { StyleSheet, Text } from 'react-native';

import Currency from 'components/currency';
import { primitive } from 'styles/constants/primitives';

import { usePaymentContext } from '../../../../../../features/payment';
import { CardTypes, IPaymentMethod } from '../../../../../../features/payment/types';
import { mapPaymentMethodCardTypes } from '../../../../../../features/payment/utils';

const useAltPaymentMethodName = (paymentMethod: IPaymentMethod | undefined) => {
  const { formatMessage } = useIntl();

  const altPaymentMethodName = useMemo(() => {
    const altPaymentMethodsMap: { name: string; isSelected: boolean }[] = [
      {
        name: formatMessage({ id: 'cash' }),
        isSelected: !!paymentMethod?.cash,
      },
      {
        name: formatMessage({ id: 'paypal' }),
        isSelected: !!paymentMethod?.paypal,
      },
      {
        name: formatMessage({ id: 'venmo' }),
        isSelected: !!paymentMethod?.venmo,
      },
      {
        name: formatMessage({ id: 'cashAppPay' }),
        isSelected: !!paymentMethod?.cashapp,
      },
    ];

    const altPaymentMethod = altPaymentMethodsMap.find(
      altPaymentMethod => altPaymentMethod.isSelected
    );

    return altPaymentMethod?.name;
  }, [formatMessage, paymentMethod]);

  return altPaymentMethodName;
};

type PaymentMethodDetailsProps = {
  paymentMethod: IPaymentMethod | undefined;
  displayFor?: 'selection' | 'selectorOption';
};
export function PaymentMethodDetails({
  paymentMethod,
  displayFor = 'selection',
}: PaymentMethodDetailsProps) {
  const { formatMessage } = useIntl();
  const { getBalanceFromPaymentMethods } = usePaymentContext();
  const altPaymentMethodName = useAltPaymentMethodName(paymentMethod);

  const isCard =
    !!paymentMethod?.credit &&
    paymentMethod?.credit?.cardType !== CardTypes.APPLE_PAY &&
    paymentMethod?.credit?.cardType !== CardTypes.GOOGLE_PAY;

  const isWallet =
    !!paymentMethod?.credit &&
    (paymentMethod?.credit?.cardType === CardTypes.APPLE_PAY ||
      paymentMethod?.credit?.cardType === CardTypes.GOOGLE_PAY);

  const cardType = mapPaymentMethodCardTypes(paymentMethod?.credit?.cardType);

  const containerStyle = StyleSheet.compose(styles.container, {
    flexDirection: displayFor === 'selection' ? 'column' : 'row',
    gap: displayFor === 'selection' ? 0 : 1,
    marginLeft: primitive.spacing.$4,
    justifyContent: 'center',
  });

  const cardTypeStyle = StyleSheet.compose(styles.cardType, {
    fontFamily:
      displayFor === 'selection' ? primitive.fontFamily.$headers : primitive.fontFamily.$body,
    fontSize: displayFor === 'selection' ? 18 : 15,
    fontWeight: displayFor === 'selection' ? 'normal' : 'light',
  });

  let alias = paymentMethod?.credit?.alias;
  if (paymentMethod?.credit?.cardType === CardTypes.APPLE_PAY) {
    alias = formatMessage({ id: 'applePay' });
  }
  if (paymentMethod?.credit?.cardType === CardTypes.GOOGLE_PAY) {
    alias = formatMessage({ id: 'googlePay' });
  }

  if (isCard) {
    return (
      <View style={containerStyle}>
        <View style={{ flexDirection: 'row', gap: primitive.spacing.$1 }}>
          <Text style={cardTypeStyle} testID="payment-method-card">
            {cardType || formatMessage({ id: 'card' })}
          </Text>
          <Text style={cardTypeStyle} testID="payment-method-alias">
            {formatMessage({ id: 'endsInRedesign' }).toLowerCase()}
            {paymentMethod?.credit?.alias}
          </Text>
        </View>
        {displayFor === 'selectorOption' && <Text style={styles.cardExp}>-</Text>}
        <Text style={styles.cardExp}>
          {`${formatMessage({ id: 'expire' })} ${paymentMethod?.credit?.expiryMonth}/${
            paymentMethod?.credit?.expiryYear
          }`}
        </Text>
      </View>
    );
  }

  if (isWallet) {
    return (
      <View style={containerStyle}>
        <Text style={cardTypeStyle} testID="payment-method-card">
          {alias}
        </Text>
      </View>
    );
  }

  if (paymentMethod?.prepaid) {
    return (
      <View style={[containerStyle, displayFor !== 'selection' && { alignItems: 'center' }]}>
        <Text style={cardTypeStyle}>{formatMessage({ id: 'giftCard' })}</Text>
        {displayFor !== 'selection' && <Text style={cardTypeStyle}> - </Text>}
        <Currency
          amount={getBalanceFromPaymentMethods(paymentMethod)}
          textProps={{
            margin: 0,
          }}
        />
      </View>
    );
  }

  // If we reached to this point we will be displaying an alternative payment method
  return (
    <View style={containerStyle}>
      <Text style={cardTypeStyle}>{altPaymentMethodName}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: 'flex-start',
  },
  cardContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: primitive.spacing.$1,
  },
  cardType: {
    color: primitive.bk.$bbqBrown,
    fontSize: 18,
    fontWeight: 'normal',
  },
  cardAlias: {
    fontFamily: primitive.fontFamily.$body,
    color: primitive.bk.$bbqBrown,
    fontSize: primitive.fontSizes.md,
    marginLeft: primitive.spacing.$1,
  },
  cardExp: {
    fontFamily: primitive.fontFamily.$body,
    color: primitive.bk.$bbqBrown,
    fontSize: primitive.fontSizes.md,
    textTransform: 'capitalize',
  },
});
